import { animeList, type Anime } from "@/mocks/anime";
import { newsList, type NewsItem } from "@/mocks/news";

export const getAnimeById = (id?: string): Anime | undefined =>
  animeList.find((item) => item.id === id);

export const getNewsById = (id?: string): NewsItem | undefined =>
  newsList.find((item) => item.id === id);

export const getAnimeByIds = (ids: string[]): Anime[] =>
  ids.map((id) => getAnimeById(id)).filter((item): item is Anime => Boolean(item));

export const getNewsByIds = (ids: string[]): NewsItem[] =>
  ids.map((id) => getNewsById(id)).filter((item): item is NewsItem => Boolean(item));

const normalize = (value: string) => (value || "").trim().toLowerCase();

export const searchAnime = (keyword: string): Anime[] => {
  const q = normalize(keyword);
  if (!q) return [];
  return animeList.filter((item) =>
    [item.title, item.synopsis, item.story, item.director, item.region, item.year, ...item.types, ...item.tags]
      .join(" ")
      .toLowerCase()
      .includes(q),
  );
};

export const searchNews = (keyword: string): NewsItem[] => {
  const q = normalize(keyword);
  if (!q) return [];
  return newsList.filter((item) =>
    [item.title, item.summary, item.category, item.source, ...item.content.map((b) => b.text || "")]
      .join(" ")
      .toLowerCase()
      .includes(q),
  );
};

export interface AnimeFilter {
  region?: string;
  year?: string;
  genre?: string;
  sort?: string;
}

export const filterAnime = (source: Anime[], filter: AnimeFilter): Anime[] => {
  const { region = "全部", year = "全部", genre = "全部", sort = "latest" } = filter;
  let result = source.filter((item) => {
    const matchRegion = region === "全部" || item.region === region;
    const matchYear =
      year === "全部"
        ? true
        : year === "2022及以前"
          ? Number(item.year) <= 2022
          : item.year === year;
    const matchGenre = genre === "全部" || item.types.includes(genre);
    return matchRegion && matchYear && matchGenre;
  });
  result = [...result].sort((a, b) => {
    if (sort === "hot") {
      return (b.rating || 0) - (a.rating || 0);
    }
    return Number(b.year) - Number(a.year);
  });
  return result;
};

export const getRelatedAnime = (current: Anime, count = 4): Anime[] => {
  return animeList
    .filter((item) => item.id !== current.id)
    .map((item) => {
      const sharedTypes = item.types.filter((t) => current.types.includes(t)).length;
      const sharedTags = item.tags.filter((t) => current.tags.includes(t)).length;
      return { item, score: sharedTypes * 2 + sharedTags };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, count)
    .map((entry) => entry.item);
};

export const getRelatedNews = (current: NewsItem, count = 4): NewsItem[] => {
  const direct = getNewsByIds(current.relatedIds);
  const rest = newsList.filter(
    (item) =>
      item.id !== current.id &&
      !direct.some((d) => d.id === item.id) &&
      (item.category === current.category || current.relatedIds.includes(item.id)),
  );
  return [...direct, ...rest].slice(0, count);
};

export const getNewsForAnime = (anime: Anime, count = 3): NewsItem[] =>
  getNewsByIds(anime.newsIds).slice(0, count);

export const featuredAnime = animeList.filter((item) => Boolean(item.banner)).slice(0, 5);

export const latestNews = (count = 6): NewsItem[] =>
  [...newsList].sort((a, b) => (a.date < b.date ? 1 : -1)).slice(0, count);

export const hotAnime = (count = 8): Anime[] =>
  [...animeList].sort((a, b) => (b.rating || 0) - (a.rating || 0)).slice(0, count);